import { z } from 'zod';

/**
 * A malformed id must answer the same 404 as somebody else's id. Prisma turns a
 * non-uuid into P2023, which is a bare 500 — and a 500 tells a prober their
 * guess was at least the right shape.
 */
export const uuid = z.string().uuid();

/** Dates cross the wire as ISO 8601 strings, never as Date or epoch millis. */
export const isoDate = z.string().datetime({ offset: true });

/**
 * `seq` is a BigInt in the database and JSON.stringify refuses to serialise
 * one. It travels as a string.
 */
export const bigintString = z.string().regex(/^\d+$/);

export const pageQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: uuid.optional(),
});
export type PageQuery = z.infer<typeof pageQuery>;

/**
 * Where a name came from. An observation and a deduction are not the same
 * claim, and the interface must be able to say which — a wrong name is worse
 * than no name.
 */
export const nameSource = z.enum([
  'RECALL',
  'SELF',
  'OBSERVED',
  'SOLE_ATTENDEE',
  'DIARIZED',
  'MENTIONED',
  'CORRECTED',
  // The name another product had on the line, carried over as it stood.
  'IMPORTED',
]);
export type NameSource = z.infer<typeof nameSource>;

/** How much to believe a commitment or a decision. Shown at rest, never on hover. */
export const evidence = z.enum(['HEARD', 'WRITTEN', 'CONFIRMED', 'ASSUMED']);
export type Evidence = z.infer<typeof evidence>;

/**
 * What the bot is doing. Recall's own status codes are explicitly NOT an enum
 * and may grow without notice, so anything unrecognised becomes UNKNOWN with
 * the raw string kept on the bot run — never a throw, and never silence.
 */
export const captureHealth = z.enum([
  'SCHEDULED',
  'JOINING',
  'WAITING_ROOM',
  'RECORDING',
  'PAUSED',
  'DONE',
  'FAILED',
  'UNKNOWN',
]);
export type CaptureHealth = z.infer<typeof captureHealth>;

/** Why nothing was recorded, for a meeting that FAILED; null otherwise (2026-10-08). */
export const captureReason = z.enum(['NOBODY_CAME', 'NOT_LET_IN', 'REMOVED', 'NO_RECORDING']);
export type CaptureReason = z.infer<typeof captureReason>;

/**
 * Every refusal a person can act on is spoken. A store that records an error
 * nobody renders is the bug this exists to close — it was in 1.0 four times and
 * cost three "it did not work" reports that were all correct refusals.
 */
export const notice = z.object({
  id: uuid,
  kind: z.string(),
  body: z.string(),
  meetingId: uuid.nullable(),
  seenAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type Notice = z.infer<typeof notice>;

/** The stretch of transcript a commitment or decision came from — never a single moment. */
export const span = z.object({
  startSegmentId: uuid.nullable(),
  endSegmentId: uuid.nullable(),
  startMs: z.number().int().nullable(),
  endMs: z.number().int().nullable(),
});
export type Span = z.infer<typeof span>;

/** The meeting whose conversation ended a commitment, named on the row. */
export const closingMeeting = z.object({ id: uuid, title: z.string(), startedAt: isoDate });
export type ClosingMeeting = z.infer<typeof closingMeeting>;

/** Every state a commitment can be in. Four of them are ends. */
/**
 * ⚠ `EXPORTED` is not `DONE`. A commitment converted into an Asana task has
 * left the ledger for the tool the work is tracked in; nobody has done it, and
 * saying so would stamp unstarted work as finished (PRD 51).
 */
export const commitmentStatus = z.enum([
  'OPEN',
  'DONE',
  'DROPPED',
  'HANDED_OVER',
  'SUPERSEDED',
  'EXPORTED',
]);
export type CommitmentStatus = z.infer<typeof commitmentStatus>;

/** Derived per reader from `ownerEmail`, never stored. */
export const direction = z.enum(['I_OWE', 'OWED_TO_ME', 'SOMEONE_ELSE']);
export type Direction = z.infer<typeof direction>;

/** Who a link admits: the invited, the company domain, or anyone. */
export const linkAccess = z.enum(['INVITED', 'DOMAIN', 'PUBLIC']);
export type LinkAccess = z.infer<typeof linkAccess>;

export const spaceKind = z.enum(['PRIVATE', 'TEAM', 'CUSTOM']);
export type SpaceKind = z.infer<typeof spaceKind>;

/** What a share grants. Attendees hold a bot recording as collaborators. */
export const noteRole = z.enum(['VIEWER', 'COLLABORATOR']);
export type NoteRole = z.infer<typeof noteRole>;

/**
 * What a reader is shown in their own language (PRD 57), and what the page
 * says about it: "Translated from Russian · show original".
 *
 * Null when everything is already in the reader's language — the common case.
 */
export const translationState = z.object({
  /** The languages the originals are written in, where the text could say. */
  from: z.array(z.string()),
  /** The reader's language: what the translated parts are shown in. */
  to: z.string(),
  /** Some of it is still being translated, and shows its original meanwhile. */
  pending: z.boolean(),
  /** The originals are shown, because the reader asked for them. */
  original: z.boolean(),
});
export type TranslationState = z.infer<typeof translationState>;
